import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useCan } from '@feedflo/access-control';
import NotificationTierSelector from '../../../molecules/NotificationTierSelector/NotificationTierSelector';
import MessageHistoryLink from '../../../molecules/MessageHistoryLink';
import LoadingSkeleton from '../../../atoms/LoadingSkeleton';
import * as Sentry from '@sentry/browser';
import './NotificationSettings.scss';

const GET_NOTIFICATION_TIER_ASSIGNMENTS_GQL = gql`
  query GetNotificationTierAssignments($user_id: uuid!, $barn_id: uuid!) {
    organization(where: { farms: { id: { _eq: $barn_id } } }) {
      id
      name
      selected_notification_plan {
        plan_id
      }
      user_notification_tier_assignments(where: { user_id: { _eq: $user_id } }) {
        id
        tier_id
        tier {
          id
          name
          public_description
        }
      }
    }
    farm(where: { id: { _eq: $barn_id } }) {
      user_notification_tier_assignments(where: { user_id: { _eq: $user_id } }) {
        id
        tier_id
        tier {
          id
          name
          public_description
        }
      }
    }
  }
`;

const UPSERT_BARN_TIER_ASSIGNMENT_GQL = gql`
  mutation UpsertBarnTierAssignment($barn_id: uuid!, $user_id: uuid!, $tier_id: uuid) {
    insert_user_barn_notification_tier_assignment_one(
      object: { barn_id: $barn_id, user_id: $user_id, tier_id: $tier_id }
      on_conflict: { constraint: user_barn_notification_tier_assignment_unique, update_columns: [tier_id] }
    ) {
      id
      tier_id
    }
  }
`;

const DELETE_BARN_TIER_ASSIGNMENT_GQL = gql`
  mutation DeleteBarnTierAssignment($barn_id: uuid!, $user_id: uuid!) {
    delete_user_barn_notification_tier_assignment(where: { barn_id: { _eq: $barn_id }, user_id: { _eq: $user_id } }) {
      affected_rows
    }
  }
`;

function NotificationSettings({ barnId, userId, onSave, refetchMainQuery }) {
  const { can } = useCan();
  const canAssignSelfBarn = barnId ? can('barn', barnId, 'barn:notfication:policy:self:assign') : false;

  const [isSaving, setIsSaving] = useState(false);

  const { data: tierData, loading: tierDataLoading } = useQuery(GET_NOTIFICATION_TIER_ASSIGNMENTS_GQL, {
    fetchPolicy: 'no-cache',
    variables: {
      user_id: userId,
      barn_id: barnId,
    },
    context: {
      headers: {
        'x-hasura-role': 'user',
      },
    },
    skip: !userId || !barnId,
  });

  const [upsertBarnTierAssignment] = useMutation(UPSERT_BARN_TIER_ASSIGNMENT_GQL, {
    refetchQueries: [GET_NOTIFICATION_TIER_ASSIGNMENTS_GQL],
    context: {
      headers: {
        'x-hasura-role': 'user',
      },
    },
    onCompleted: () => {
      if (onSave) {
        onSave();
      }
      if (refetchMainQuery) {
        refetchMainQuery();
      }
      setIsSaving(false);
    },
    onError: (error) => {
      Sentry.captureException(error);
      console.error('Failed to save notification settings:', error);
      setIsSaving(false);
    },
  });

  const [deleteBarnTierAssignment] = useMutation(DELETE_BARN_TIER_ASSIGNMENT_GQL, {
    refetchQueries: [GET_NOTIFICATION_TIER_ASSIGNMENTS_GQL],
    context: {
      headers: {
        'x-hasura-role': 'user',
      },
    },
    onCompleted: () => {
      if (onSave) {
        onSave();
      }
      if (refetchMainQuery) {
        refetchMainQuery();
      }
      setIsSaving(false);
    },
    onError: (error) => {
      Sentry.captureException(error);
      console.error('Failed to save notification settings:', error);
      setIsSaving(false);
    },
  });

  const organization = tierData?.organization?.[0];
  const orgTierAssignment = organization?.user_notification_tier_assignments?.[0];
  const barnTierAssignment = tierData?.farm?.[0]?.user_notification_tier_assignments?.[0];
  const notificationPlanId = organization?.selected_notification_plan?.plan_id || null;
  const effectiveTierId = barnTierAssignment?.tier_id || orgTierAssignment?.tier_id || null;
  const hasBarnOverride = !!barnTierAssignment;
  const defaultTierId = orgTierAssignment?.tier_id || null;

  const handleTierChange = async (tierId) => {
    if (isSaving) return;

    setIsSaving(true);

    try {
      // If user selected the org tier (the default), delete barn override
      if (tierId === orgTierAssignment?.tier_id && hasBarnOverride) {
        await deleteBarnTierAssignment({
          variables: {
            barn_id: barnId,
            user_id: userId,
          },
        });
      } else if (tierId && tierId !== orgTierAssignment?.tier_id) {
        // If user selected a different tier, create/update barn override
        await upsertBarnTierAssignment({
          variables: {
            barn_id: barnId,
            user_id: userId,
            tier_id: tierId,
          },
        });
      } else if (!tierId && hasBarnOverride) {
        // If user cleared selection and has override, delete it
        await deleteBarnTierAssignment({
          variables: {
            barn_id: barnId,
            user_id: userId,
          },
        });
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error('Failed to save notification settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (tierDataLoading) {
    return (
      <div className="NotificationSettings">
        <div className="NotificationSettings-section">
          {/* <b className="NotificationSettings-title">Notifications:</b> */}
          <LoadingSkeleton style={{ width: '100%', height: '40px', borderRadius: '4px', marginTop: '10px' }} />
        </div>
      </div>
    );
  }

  return (
    <div className="NotificationSettings">
      <div className="NotificationSettings-section">
        {notificationPlanId ? (
          <div style={{ marginTop: '10px' }}>
            <MessageHistoryLink filters={{ barnId, userId }} />
            <NotificationTierSelector
              selectedTierId={effectiveTierId}
              onTierChange={(e) => {
                const value = typeof e === 'string' ? e : e?.target?.value;
                handleTierChange(value || null);
              }}
              planId={notificationPlanId}
              label="Notification Policy"
              showDescription={false}
              disabled={!canAssignSelfBarn || isSaving}
              defaultTierId={defaultTierId}
            />
          </div>
        ) : null}

        <div className="NotificationSettings-footer">
          {!canAssignSelfBarn && (
            <div className="NotificationSettings-description">
              You do not have permission to change your notification level assignments for this barn.
            </div>
          )}
          {!notificationPlanId && (
            <div className="NotificationSettings-description">
              No Notification Plan Selected,
              <br />
              Contact Support to set one up.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

NotificationSettings.propTypes = {
  barnId: PropTypes.string.isRequired,
  userId: PropTypes.string.isRequired,
  onSave: PropTypes.func,
  refetchMainQuery: PropTypes.func,
};

export default NotificationSettings;
